// /src/reports/WastageReport.js
import React, { useState, useEffect } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, addDays } from "date-fns";
import { toast } from "react-toastify";

// OneStop week start date
const ONE_STOP_WEEK1_START = new Date("2025-02-23"); // Sunday

function getOneStopWeekInfo(date) {
  const diffDays = Math.floor(
    (date - ONE_STOP_WEEK1_START) / (1000 * 60 * 60 * 24)
  );
  const weekNum = Math.floor(diffDays / 7) + 1;
  const weekStart = addDays(ONE_STOP_WEEK1_START, (weekNum - 1) * 7);
  const weekEnd = addDays(weekStart, 6);
  return { weekNum, weekStart, weekEnd };
}

export default function WastageReport({ fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("daily");

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, viewType]);

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("wastage")
      .select("*")
      .gte("date", fromDate)
      .lte("date", toDate);

    if (error) {
      toast.error("Error fetching wastage data");
      setLoading(false);
      return;
    }

    const processed = data.map((w) => ({
      date: format(new Date(w.date), "yyyy-MM-dd"),
      amount: parseFloat(w.amount) || 0,
      notes: w.notes || ""
    }));

    setRows(processed);
    setLoading(false);
  }

  function groupByWeek(data) {
    const grouped = {};
    data.forEach((row) => {
      const { weekNum, weekStart, weekEnd } = getOneStopWeekInfo(
        new Date(row.date)
      );
      const key = `Week ${weekNum} (${format(weekStart, "dd MMM")} - ${format(
        weekEnd,
        "dd MMM"
      )})`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }

  function exportCSV() {
    const headers = ["Date/Week", "Amount (£)", "Notes"];
    let csvRows = [headers.join(",")];

    if (viewType === "daily") {
      rows.forEach((r) => {
        csvRows.push([
          r.date,
          r.amount.toFixed(2),
          `"${r.notes.replace(/"/g, '""')}"`
        ].join(","));
      });
    } else {
      const weeklyGroups = groupByWeek(rows);
      Object.keys(weeklyGroups).forEach((week) => {
        weeklyGroups[week].forEach((r) => {
          csvRows.push([
            week,
            r.amount.toFixed(2),
            `"${r.notes.replace(/"/g, '""')}"`
          ].join(","));
        });
      });
    }

    saveAs(
      new Blob([csvRows.join("\n")], { type: "text/csv" }),
      `wastage_report_${format(new Date(), "yyyyMMdd_HHmm")}.csv`
    );
  }

  if (loading) return <p>Loading Wastage Report...</p>;

  if (viewType === "daily") {
    const total = rows.reduce((sum, r) => sum + r.amount, 0);

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount (£)</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{r.amount.toFixed(2)}</td>
                <td>{r.notes}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
              <td>Total</td>
              <td>{total.toFixed(2)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    const weeklyGroups = groupByWeek(rows);
    const grandTotal = rows.reduce((sum, r) => sum + r.amount, 0);

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        {Object.keys(weeklyGroups).map((week) => {
          const weekRows = weeklyGroups[week];
          const weekTotal = weekRows.reduce((sum, r) => sum + r.amount, 0);

          return (
            <div key={week} style={{ marginBottom: "20px" }}>
              <h3>{week}</h3>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount (£)</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {weekRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.date}</td>
                      <td>{r.amount.toFixed(2)}</td>
                      <td>{r.notes}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                    <td>Weekly Total</td>
                    <td>{weekTotal.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        <h3 style={{ marginTop: "10px" }}>
          Grand Total: £{grandTotal.toFixed(2)}
        </h3>
      </div>
    );
  }
}
